<script setup lang="ts">
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import CommissionCountriesTabs from '@modules/affiliate-program-commissions/submodules/edit/components/CommissionCountriesTabs/CommissionCountriesTabs.vue';
import { ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME } from '@modules/affiliates-approved/constants';
import VAffiliateTrackersSettingsTabs from '@modules/affiliates-approved/submodules/trackers-settings/components/VAffiliateTrackersSettingsTabs/VAffiliateTrackersSettingsTabs.vue';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS } from '@modules/affiliates-approved/submodules/trackers-settings/constants';
import VChangeHistory from '@modules/change-history/components/VChangeHistory/VChangeHistory.vue';
import VFormMultiselectFilterWidget from '@modules/form-widgets/components/VFormMultiselectFilterWidget/VFormMultiselectFilterWidget.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';

import {
  AFFILIATES_TRACKER_GROUPS_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS,
} from '../../constants';
import { useTrackerGroupsForm, useTrackerGroupsHistory } from './composables';

const {
  activeFilterIndex,
  isSaveLoading,
  errors,
  editFromPermission,
  activeTrackerGroupFilter,
  formSchema,
  formGroups,
  trackerGroupsTabs,
  displayFilterErrors,
  trackersOptions,
  warnMessage,
  clearError,
  onFormChange,
  onSubmit,
  onFilterAdd,
  onFilterSelect,
  onFilterExpandToggle,
  onFilterRemove,
  onChangeFilterName,
  onChangeValue,
} = useTrackerGroupsForm();

const {
  historyOfTrackerGroupsChanges,
  isHistoryTrackerGroupsLoading,
  onHistoryDelete,
} = useTrackerGroupsHistory();
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateTrackerGroupsPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_HEADER')"
  >
    <VAffiliateTrackersSettingsTabs />
    <VForm
      :schema="formSchema"
      :groups="formGroups"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_SAVE')"
      :is-loading="isSaveLoading"
      :disabled="!editFromPermission"
      :custom-errors="errors"
      @submit="onSubmit"
      @change="onFormChange"
      @interacted="clearError"
    >
      <template #trackerGroupsIndex="{ className, readonly }">
        <div class="mb-6">
          <CommissionCountriesTabs
            :tabs="trackerGroupsTabs"
            :class="className"
            :readonly="readonly || !editFromPermission"
            :errors="displayFilterErrors"
            :translate-key="TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS"
            @add="onFilterAdd"
            @select="onFilterSelect"
            @expand-toggle="onFilterExpandToggle"
            @remove="onFilterRemove"
          />
        </div>
      </template>
      <template #trackerListValues="{ className, readonly }">
        <div class="mb-4">
          <VFormMultiselectFilterWidget
            v-if="activeTrackerGroupFilter?.expanded"
            :key="activeFilterIndex"
            :options="trackersOptions"
            :filter-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_GROUPS, 'FORM_GROUPS_NAME_INPUT_LABEL')"
            :filter-value="activeTrackerGroupFilter?.label"
            :errors="activeTrackerGroupFilter?.errors?.validSelection"
            :class="className"
            :readonly="readonly || !editFromPermission"
            required-name
            :disabled="!editFromPermission"
            @change-name="onChangeFilterName"
            @change-value="onChangeValue"
          />
        </div>
      </template>
      <template #prefooter>
        <VMessage
          v-if="warnMessage"
          class="input-max-width"
          severity="warn"
        >
          {{ warnMessage }}
        </VMessage>
      </template>
      <template #secondaryBtn>
        <VBackButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_BACK')"
          skip-history
          :to="{
            name: ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME,
          }"
        />
      </template>
    </VForm>
  </VCard>

  <VLoader
    :loading="isHistoryTrackerGroupsLoading"
    class="mt-2"
  >
    <VChangeHistory
      :list="historyOfTrackerGroupsChanges"
      :delete-permission="AFFILIATES_TRACKER_GROUPS_PERMISSIONS.DELETE"
      @delete="onHistoryDelete"
    />
  </VLoader>
</template>
